import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator } from 'react-native';
import moment from 'moment';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import { accessService } from '../../../services/central-api/access';
import { cameraService } from '../../../services/central-api/cameras';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { Tabs } from '../../../components/Tabs';
import { ScrollView } from 'react-native-web-hover';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { SelectableItem } from '../../../components/SelectableList';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import '../../../styles/time-picker.css';
import MyAppText from '../../../components/MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import MapPolygonDropdown from '../../../components/MapPolygonDropdown';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { SelectablePaginatedList } from '../../../components/SelectableListPaginated';
import { useInfiniteQuery } from '@tanstack/react-query';
import useDebounce from '../../../components/hooks/useDebounce';
import CameraTagsDropdown from '../../../components/Filters/Cameras/CameraTagsDropdown';
import { useNavigation, NavigationProp, useFocusEffect, useIsFocused, RouteProp, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import BasicModal from '../../../components/BasicModal';
import FontWeight from '../../../styles/font-weights';

const PAGE_SIZE = 100;

export default function UpsertAlertGroupModal() {
    const { styles, theme } = useStyles(styleSheet);
    const navigation = useNavigation<NavigationProp<CamerasParamList>>();
    const route = useRoute<RouteProp<CamerasParamList, 'UpsertAlertGroupModal'>>();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [times, setTimes] = useState<AlertGroupTimes>({});

    const [selectableCameras, setSelectableCameras] = useState<SelectableItem[]>([]);
    const [selectedCameras, setSelectedCameras] = useState<SelectableItem[]>([]);

    const [selectedPolygon, setSelectedPolygon] = useState<number>(0);
    const [isPolygonPickerOpen, setIsPolygonPickerOpen] = useState(false);
    const [polygonType, setPolygonType] = useState<string | null>(null);
    const [isLoadingPolygon, setIsLoadingPolygon] = useState<boolean>(false);
    const [selectedPolygons, setSelectedPolygons] = useState<MapPolygon[]>([]);
    const [selectedTags, setSelectedTags] = useState<number[]>([]);

    const isFetchingRef = useRef(false);
    const [textFilter, setTextFilter] = useState<string>('');
    const debouncedFilter = useDebounce(textFilter, 500);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [isModalVisible, setModalVisible] = useState<boolean>(true);
    const [alertGroupId, setAlertGroupId] = useState<number>();

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        setModalVisible(true);
    }, [isFocused]));

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['cameras', selectedPolygon, selectedTags, debouncedFilter],
        enabled: isModalVisible,
        queryFn: ({ pageParam = 0 }) =>
            accessService.getPaginatedAlertGroupCameras({
                page: pageParam,
                limit: PAGE_SIZE,
                textFilter: debouncedFilter || undefined,
                mapPolygonId: selectedPolygon != 0 ? selectedPolygon : undefined,
                hasAreaInvasion: true,
                tagIds: selectedTags.length ? selectedTags : undefined
            }),
        getNextPageParam: (lastPage, pages) =>
            lastPage.rows.length == PAGE_SIZE ? pages.length : undefined,
    });

    useEffect(() => {
        setAlertGroupId(route.params?.id);
    }, [route.params]);

    function isNameInvalid(value: string) {
        return !value || value == '';
    }

    function isFormValid() {
        if (isNameInvalid(name)) {
            return false;
        }

        return Object.values(times).every(dayTimes =>
            dayTimes.every(interval =>
                moment(interval.start, 'HH:mm').isSameOrBefore(moment(interval.end, 'HH:mm'), 'minute')
            )
        );
    }

    function clearValues() {
        setName('');
        setDescription('');
        setTimes({});
        setSelectedCameras([]);
        setSelectedPolygon(0);
        setSelectedTags([]);
    }

    async function getAlertGroup(alertGroupId: number) {
        const alertGroup = await cameraService.getAlertGroup(alertGroupId);
        setName(alertGroup.name);
        setDescription(alertGroup.description);
        setTimes(alertGroup.times);

        const selectedCameras = alertGroup.cameras.map((camera) => {
            return {
                id: camera.id,
                name: camera.title
            };
        });
        setSelectedCameras(selectedCameras);

        return alertGroup;
    }

    useEffect(() => {
        if (!alertGroupId || !isModalVisible) {
            return;
        }

        getAlertGroup(alertGroupId);
    }, [alertGroupId, isModalVisible]);

    useEffect(() => {
        if (!isModalVisible || !data) {
            return;
        }

        const items = data.pages
            .flatMap(page => page.rows)
            .filter(cam => !selectedCameras.some(sel => sel.id === cam.id))
            .map(cam => ({
                id: cam.id,
                name: cam.title,
            }));

        setSelectableCameras(items);
    }, [data, selectedCameras, isModalVisible]);

    async function formSubmit() {
        try {
            if (alertGroupId) {
                await cameraService.updateAlertGroup({
                    id: alertGroupId,
                    name,
                    description,
                    times,
                    cameras: selectedCameras.map(c => String(c.id)),
                });
            } else {
                await cameraService.createAlertGroup({
                    name,
                    description,
                    times,
                    cameras: selectedCameras.map(c => String(c.id)),
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            goBack();
            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function addDayInterval(weekDay: number) {
        if (!Array.isArray(times[weekDay])) {
            times[weekDay] = [];
        }

        times[weekDay].push({ start: '', end: '' });
        setTimes({ ...times });
    }

    function renderDayIntervals(weekDay: number) {
        const dayTimes = times[weekDay] ?? [];

        return dayTimes.map((_, index) => {
            return (
                <View key={index} style={styles.timeIntervalContainer}>
                    <View style={styles.timeIntervalContent}>
                        <MyAppText>{translate('start')}</MyAppText>
                        <TimePicker
                            disableClock={true}
                            format='HH:mm'
                            locale='pt-BR'
                            maxDetail='minute'
                            className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? 'react-time-picker__wrapper-light' : 'react-time-picker__wrapper-dark'}
                            value={times[weekDay][index].start}
                            onChange={(value) => {
                                if (value) {
                                    times[weekDay][index].start = value;
                                    setTimes({ ...times });
                                }
                            }}
                        />
                    </View>
                    <View style={styles.timeIntervalContent}>
                        <MyAppText>{translate('end')}</MyAppText>
                        <TimePicker
                            disableClock={true}
                            format='HH:mm'
                            locale='pt-BR'
                            maxDetail='minute'
                            className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? 'react-time-picker__wrapper-light' : 'react-time-picker__wrapper-dark'}
                            value={times[weekDay][index].end}
                            onChange={(value) => {
                                if (value) {
                                    times[weekDay][index].end = value;
                                    setTimes({ ...times });
                                }
                            }}
                        />
                    </View>
                    <TouchableOpacity style={styles.removeIntervalButton} onPress={() => {
                        times[weekDay].splice(index, 1);
                        setTimes({ ...times });
                    }}>
                        <MyAppText>
                            <FontAwesomeIcon fontSize={20} icon={faXmark} />
                        </MyAppText>
                    </TouchableOpacity>
                </View >
            );
        });
    }

    useEffect(() => {
        setTextFilter(textFilter);
    }, [debouncedFilter]);

    useEffect(() => {
        setTextFilter(debouncedSearch);
        setSelectableCameras([]);
    }, [debouncedSearch]);

    useEffect(() => {
        isFetchingRef.current = false;
    }, [textFilter, selectedPolygon]);

    function goBack() {
        setModalVisible(false);
        setTimeout(() => {
            navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AlertGroups', { page: 0, limit: 25 });
        }, 500);
    }

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('alertGroup')}
            width='large'
            disableScrollView={true}
            primaryButton={{
                text: translate('save'),
                onClick: formSubmit,
                disabled: !isFormValid(),
            }}
            cancelButton={{
                onClick: () => {
                    goBack();
                    clearValues();
                }
            }}
        >
            <View style={{ height: 520 }}>
                <Tabs tabs={[{
                    key: 'details',
                    label: translate('details')
                }, {
                    key: 'cameras',
                    label: translate('cameras')
                }, {
                    key: 'silence',
                    label: translate('silence')
                }]}>
                    {({ selectedTab }) => {
                        if (selectedTab == 'details') {
                            return (<>
                                <FormInput
                                    label={translate('zone')}
                                    placeholder={translate('zone')}
                                    value={name}
                                    onChangeText={setName}
                                    invalid={isNameInvalid}
                                />
                                <FormInput
                                    label={translate('description')}
                                    placeholder={translate('description')}
                                    value={description}
                                    onChangeText={setDescription}
                                    invalid={() => false}
                                />
                            </>);
                        } else if (selectedTab == 'cameras') {
                            return <View style={{ rowGap: 10, flex: 1 }}>
                                <View style={{ flexDirection: 'column', gap: 8, zIndex: 2, alignItems: 'center' }}>
                                    <View style={{ width: '100%', zIndex: 4 }}>
                                        <MapPolygonDropdown
                                            label={translate('subRegions')}
                                            dropDownDirection='BOTTOM'
                                            setPolygons={setSelectedPolygons}
                                            setPolygonType={(type) => {
                                                setPolygonType(type);
                                                setSelectedPolygon(0);
                                            }}
                                            setPendingLoading={setIsLoadingPolygon}
                                            showTooltip
                                            zIndex={4}
                                            labelStyle={styles.selectLabelStyle}
                                            viewStyle={{ gap: 8, minWidth: 'auto' }}
                                        />
                                    </View>
                                    {isLoadingPolygon
                                        ? <ActivityIndicator size="small" color={theme.colors.primary['500']} />
                                        : selectedPolygons.length > 0 && <View style={{ width: '100%', zIndex: 3, gap: 8 }}>
                                            <MyAppText>{polygonType}</MyAppText>
                                            <MyDropDownPicker
                                                placeholderColor={theme.colors.placeholder}
                                                placeholder={translate('filter')}
                                                open={isPolygonPickerOpen}
                                                items={selectedPolygons.map(p => {
                                                    return { label: p.name, value: p.id };
                                                })}
                                                setOpen={setIsPolygonPickerOpen}
                                                value={selectedPolygon}
                                                setValue={setSelectedPolygon}
                                                multiple={false}
                                                searchable={true}
                                                labelStyle={styles.selectLabelStyle}
                                            />
                                        </View>}
                                    <View style={{ width: '100%', zIndex: 2 }}>
                                        <CameraTagsDropdown
                                            value={selectedTags}
                                            setValue={setSelectedTags}
                                            zIndex={2}
                                            labelStyle={styles.selectLabelStyle}
                                            label='cameraTags'
                                        />
                                    </View>
                                </View>
                                <SelectablePaginatedList
                                    selectableLabel={translate('cameras')}
                                    selectedLabel={translate('selectedCameras')}
                                    selectableItems={selectableCameras}
                                    selectedItems={selectedCameras}
                                    onAddItem={(item) =>
                                        setSelectedCameras(prev => [...prev, item])
                                    }
                                    onRemoveItem={(item) =>
                                        setSelectedCameras(prev => prev.filter(i => i.id !== item.id))
                                    }
                                    onAddAll={() => {
                                        setSelectedCameras(prev => [...prev, ...selectableCameras]);
                                    }}
                                    onRemoveAll={() => {
                                        setSelectedCameras([]);
                                    }}
                                    onSelectableSearchChange={setSearch}
                                    onLoadMoreSelectable={async () => {
                                        if (!hasNextPage || isFetchingRef.current) {
                                            return;
                                        }
                                        isFetchingRef.current = true;
                                        try {
                                            await fetchNextPage();
                                        } finally {
                                            isFetchingRef.current = false;
                                        }
                                    }}
                                    isLoadingMoreSelectable={isFetchingNextPage}
                                />
                            </View>;
                        } else if (selectedTab == 'silence') {
                            return <ScrollView style={{ rowGap: 10, flex: 1 }}>
                                <View style={{ paddingBottom: 15 }}>
                                    <MyAppText style={styles.silenceTabDescription}>{translate('silenceTabDescription')}</MyAppText>
                                </View>
                                <View>
                                    <MyAppText style={[styles.weekDayText, { paddingTop: 0 }]}>{translate('monday')}</MyAppText>
                                    {renderDayIntervals(1)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(1)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                                <View>
                                    <MyAppText style={styles.weekDayText}>{translate('tuesday')}</MyAppText>
                                    {renderDayIntervals(2)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(2)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                                <View>
                                    <MyAppText style={styles.weekDayText}>{translate('wednesday')}</MyAppText>
                                    {renderDayIntervals(3)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(3)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                                <View>
                                    <MyAppText style={styles.weekDayText}>{translate('thursday')}</MyAppText>
                                    {renderDayIntervals(4)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(4)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                                <View>
                                    <MyAppText style={styles.weekDayText}>{translate('friday')}</MyAppText>
                                    {renderDayIntervals(5)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(5)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                                <View>
                                    <MyAppText style={styles.weekDayText}>{translate('saturday')}</MyAppText>
                                    {renderDayIntervals(6)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(6)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                                <View>
                                    <MyAppText style={styles.weekDayText}>{translate('sunday')}</MyAppText>
                                    {renderDayIntervals(0)}
                                    <TouchableOpacity style={styles.addIntervalButton} onPress={() => addDayInterval(0)}>
                                        <MyAppText>+ {translate('addInterval')}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                            </ScrollView>;
                        }
                    }}
                </Tabs>
            </View>
        </BasicModal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    weekDayText: {
        fontSize: 20,
        paddingTop: 15,
        paddingBottom: 15,
        fontWeight: FontWeight.bold,
    },
    timeIntervalContainer: {
        gap: 5,
        flexDirection: 'row'
    },
    timeIntervalContent: {
        width: 125
    },
    addIntervalButton: {
        width: 250,
        paddingTop: 10
    },
    removeIntervalButton: {
        paddingBottom: 4,
        paddingLeft: 4,
        justifyContent: 'flex-end',
    },
    silenceTabDescription: {
        fontSize: 12
    },
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: FontWeight.semiBold,
        fontSize: 14,
    },
}));
